import { useState, useEffect, useRef } from "react";
import api, { apiErr } from "@/lib/api";
import { uploadArtFile, artUrl } from "@/lib/uploadArt";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { HelpHint } from "@/components/HelpHint";
import { Plus, Trash2, Save, X, Layers, Link2, Link2Off } from "lucide-react";
import { toast } from "sonner";

const BLANK = { name: "", trim_w: 3.5, trim_h: 2, unit: "in", bleed: 0.125, sides: 1,
  orientation: "portrait", category: "", subcategory: "", product_ids: [], slots: [], editable_fields: [], notes: "", active: true };

export const TemplatesTab = () => {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // template object or null
  const [saving, setSaving] = useState(false);
  const [bgBusy, setBgBusy] = useState(false);

  const load = async () => {
    try { const r = await api.get("/studio/templates"); setList(r.data.templates || []); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ ...BLANK, slots: [] });
  const edit = (t) => setEditing(JSON.parse(JSON.stringify(t)));

  const save = async () => {
    if (!editing.name?.trim()) { toast.error("Template needs a name"); return; }
    setSaving(true);
    try {
      const body = { ...editing, trim_w: +editing.trim_w, trim_h: +editing.trim_h, bleed: +editing.bleed,
        sides: +editing.sides, slots: (editing.slots || []).map((s) => ({ x: +s.x, y: +s.y, w: +s.w, h: +s.h, rotation: +s.rotation || 0, side: +s.side || 1, page: (s.page === "" || s.page == null) ? null : +s.page })),
        editable_fields: (editing.editable_fields || []).map((f) => ({ id: f.id, type: f.type, label: f.label || "", x: +f.x, y: +f.y, w: +f.w, h: +f.h, default: f.default || "", font_size: +f.font_size || 12, color: f.color || "#000000", align: f.align || "left" })) };
      if (editing.id) await api.put(`/studio/templates/${editing.id}`, body);
      else await api.post("/studio/templates", body);
      toast.success("Template saved");
      setEditing(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await api.delete(`/studio/templates/${t.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const setF = (k, v) => setEditing((p) => ({ ...p, [k]: v }));
  const [tRatioLock, setTRatioLock] = useState(false);
  const tRatio = useRef(0);
  const onTW = (v) => { setF("trim_w", v); if (tRatioLock && +v > 0 && tRatio.current > 0) setF("trim_h", Math.round((+v / tRatio.current) * 100) / 100); };
  const onTH = (v) => { setF("trim_h", v); if (tRatioLock && +v > 0 && tRatio.current > 0) setF("trim_w", Math.round((+v * tRatio.current) * 100) / 100); };
  const toggleTL = () => { const on = !tRatioLock; setTRatioLock(on); if (on) { const w = +editing.trim_w, h = +editing.trim_h; tRatio.current = (w > 0 && h > 0) ? w / h : 1; } };
  const addSlot = () => setEditing((p) => ({ ...p, slots: [...(p.slots || []), { x: 0, y: 0, w: +p.trim_w, h: +p.trim_h, rotation: 0, side: 1, page: "" }] }));
  const setSlot = (i, k, v) => setEditing((p) => ({ ...p, slots: p.slots.map((s, idx) => idx === i ? { ...s, [k]: v } : s) }));
  const rmSlot = (i) => setEditing((p) => ({ ...p, slots: p.slots.filter((_, idx) => idx !== i) }));

  const ingestBg = async (f) => {
    if (!f) return;
    setBgBusy(true);
    try {
      const up = await uploadArtFile(f);
      const fd = new FormData(); fd.append("file_id", up.file_id);
      const r = await api.post("/studio/ingest-design", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((p) => ({ ...p, background_file_id: r.data.background_file_id }));
      toast.success("Design background added");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBgBusy(false); }
  };
  const addField = () => setEditing((p) => ({ ...p, editable_fields: [...(p.editable_fields || []), { id: `f${Date.now().toString(36)}`, type: "text", label: "", x: 0.3, y: 0.3, w: 2, h: 0.4, default: "", font_size: 14, color: "#000000", align: "left" }] }));
  const setField = (i, k, v) => setEditing((p) => ({ ...p, editable_fields: p.editable_fields.map((f, idx) => idx === i ? { ...f, [k]: v } : f) }));
  const rmField = (i) => setEditing((p) => ({ ...p, editable_fields: p.editable_fields.filter((_, idx) => idx !== i) }));

  if (editing) {
    return (
      <div className="space-y-4" data-testid="template-form">
        <div className="flex items-center justify-between">
          <h3 className="font-head text-xl font-bold flex items-center gap-2">
            {editing.id ? "Edit template" : "New template"}
            <HelpHint side="right">A template is your production layout (size + bleed + sides). Define <b>slots</b> to tell the system where each client page goes and how it's rotated. Guides (safe/cut/bleed) are visual only and never printed.</HelpHint>
          </h3>
          <Button variant="ghost" size="sm" onClick={() => setEditing(null)} data-testid="template-cancel"><X size={16} /> Cancel</Button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Name" hint="A clear name you'll recognize, e.g. 'Business Card 3.5x2 double-sided'.">
            <Input data-testid="tpl-name" value={editing.name} onChange={(e) => setF("name", e.target.value)} />
          </Field>
          <Field label="Unit" hint="Measurement unit for size &amp; bleed.">
            <select className="w-full h-10 rounded-lg border px-2 text-sm" data-testid="tpl-unit" value={editing.unit} onChange={(e) => setF("unit", e.target.value)}>
              <option value="in">in</option><option value="mm">mm</option><option value="cm">cm</option>
            </select>
          </Field>
          <Field label="Trim width" hint="Final cut width (without bleed)."><Input type="number" data-testid="tpl-w" value={editing.trim_w} onChange={(e) => onTW(e.target.value)} /></Field>
          <Field label="Trim height" hint="Final cut height (without bleed)."><Input type="number" data-testid="tpl-h" value={editing.trim_h} onChange={(e) => onTH(e.target.value)} /></Field>
          <Field label="Bleed" hint="Extra background around the trim (not always 0.25). Your slots should reach into it for full-bleed cuts."><Input type="number" data-testid="tpl-bleed" value={editing.bleed} onChange={(e) => setF("bleed", e.target.value)} /></Field>
          <Field label="Sides" hint="1 = single-sided, 2 = front &amp; back. Each side becomes one PDF page in the output."><Input type="number" min="1" max="2" data-testid="tpl-sides" value={editing.sides} onChange={(e) => setF("sides", e.target.value)} /></Field>
          <Field label="Category" hint="Group templates, e.g. 'Business Cards', 'Flyers'."><Input data-testid="tpl-cat" value={editing.category} onChange={(e) => setF("category", e.target.value)} /></Field>
          <Field label="Subcategory" hint="Optional finer grouping, e.g. 'Tri-fold'."><Input data-testid="tpl-subcat" value={editing.subcategory} onChange={(e) => setF("subcategory", e.target.value)} /></Field>
        </div>
        <button type="button" onClick={toggleTL} className={`inline-flex items-center gap-1 text-xs -mt-1 ${tRatioLock ? "text-[#2495D3]" : "text-slate-400 hover:text-slate-600"}`} data-testid="tpl-ratio-lock">{tRatioLock ? <Link2 size={14} /> : <Link2Off size={14} />} Lock trim W:H ratio</button>

        <div className="rounded-xl border p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-mono uppercase text-slate-500 flex items-center gap-1">Slots
              <HelpHint side="top">Each slot is a rectangle (in your unit) measured from the <b>top-left of the full bleed page</b>: X, Y, W, H, plus rotation and which side/client-page it holds. Leave <b>Page</b> empty to fill client pages in order. No slots = one full-page slot per side.</HelpHint>
            </span>
            <Button size="sm" variant="outline" onClick={addSlot} data-testid="tpl-add-slot"><Plus size={14} /> Add slot</Button>
          </div>
          {(editing.slots || []).length === 0 && <p className="text-xs text-slate-400">No slots — client pages will fill each side automatically.</p>}
          {(editing.slots || []).map((s, i) => (
            <div key={i} className="grid grid-cols-12 gap-1 items-center mb-1" data-testid={`tpl-slot-${i}`}>
              {["x", "y", "w", "h"].map((k) => (
                <Input key={k} className="col-span-2 h-8 text-xs num" type="number" placeholder={k.toUpperCase()} value={s[k]} onChange={(e) => setSlot(i, k, e.target.value)} data-testid={`tpl-slot-${i}-${k}`} />
              ))}
              <select className="col-span-2 h-8 rounded border text-xs" value={s.rotation} onChange={(e) => setSlot(i, "rotation", e.target.value)} data-testid={`tpl-slot-${i}-rot`}>
                {[0, 90, 180, 270].map((r) => <option key={r} value={r}>{r}°</option>)}
              </select>
              <Input className="col-span-1 h-8 text-xs num" type="number" placeholder="Side" value={s.side} onChange={(e) => setSlot(i, "side", e.target.value)} data-testid={`tpl-slot-${i}-side`} />
              <Input className="col-span-1 h-8 text-xs num" type="number" placeholder="Pg" value={s.page ?? ""} onChange={(e) => setSlot(i, "page", e.target.value)} data-testid={`tpl-slot-${i}-page`} />
              <button className="col-span-0 text-slate-400 hover:text-red-500" onClick={() => rmSlot(i)} data-testid={`tpl-slot-${i}-rm`}><Trash2 size={14} /></button>
            </div>
          ))}
        </div>

        <div className="rounded-xl border p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-mono uppercase text-slate-500 flex items-center gap-1">Design &amp; editable fields
              <HelpHint side="top">Upload your finished design (PSD / PDF / AI / image) as the locked <b>background</b>, then add <b>editable fields</b> the client can change: text, image/logo, color, or QR. Positions use the same units, measured from the top-left of the full bleed page. The background &amp; guides never move.</HelpHint>
            </span>
            <div className="flex gap-2">
              <label className="inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-xs cursor-pointer hover:border-[#2495D3]" data-testid="tpl-bg-upload">
                {bgBusy ? "Uploading…" : (editing.background_file_id ? "Replace background" : "Upload background")}
                <input type="file" accept="application/pdf,image/png,image/jpeg,.psd,.ai" className="hidden" onChange={(e) => { ingestBg(e.target.files?.[0]); e.target.value = ""; }} />
              </label>
              <Button size="sm" variant="outline" onClick={addField} data-testid="tpl-add-field"><Plus size={14} /> Add field</Button>
            </div>
          </div>
          {editing.background_file_id
            ? <img src={artUrl(editing.background_file_id)} alt="bg" className="max-h-40 rounded border mb-2" data-testid="tpl-bg-preview" />
            : <p className="text-xs text-slate-400 mb-2">No background yet — upload your design to lock it as the base.</p>}
          {(editing.editable_fields || []).length === 0 && <p className="text-xs text-slate-400">No editable fields. Add one so clients can personalize text, logo, color or a QR.</p>}
          {(editing.editable_fields || []).map((f, i) => (
            <div key={f.id} className="rounded-lg border p-2 mb-2" data-testid={`tpl-field-${i}`}>
              <div className="grid grid-cols-12 gap-1 items-center">
                <select className="col-span-2 h-8 rounded border text-xs" value={f.type} onChange={(e) => setField(i, "type", e.target.value)} data-testid={`tpl-field-${i}-type`}>
                  <option value="text">Text</option><option value="image">Image</option><option value="color">Color</option><option value="qr">QR</option>
                </select>
                <Input className="col-span-3 h-8 text-xs" placeholder="Label" value={f.label} onChange={(e) => setField(i, "label", e.target.value)} data-testid={`tpl-field-${i}-label`} />
                {["x", "y", "w", "h"].map((k) => (
                  <Input key={k} className="col-span-1 h-8 text-xs num" type="number" placeholder={k.toUpperCase()} value={f[k]} onChange={(e) => setField(i, k, e.target.value)} data-testid={`tpl-field-${i}-${k}`} />
                ))}
                <Input className="col-span-2 h-8 text-xs" placeholder="Default" value={f.default} onChange={(e) => setField(i, "default", e.target.value)} data-testid={`tpl-field-${i}-default`} />
                <button className="col-span-1 text-slate-400 hover:text-red-500 justify-self-end" onClick={() => rmField(i)} data-testid={`tpl-field-${i}-rm`}><Trash2 size={14} /></button>
              </div>
              {f.type === "text" && (
                <div className="grid grid-cols-12 gap-1 items-center mt-1">
                  <span className="col-span-2 text-[10px] text-slate-400">size(pt)</span>
                  <Input className="col-span-2 h-7 text-xs num" type="number" value={f.font_size} onChange={(e) => setField(i, "font_size", e.target.value)} data-testid={`tpl-field-${i}-fs`} />
                  <span className="col-span-1 text-[10px] text-slate-400">color</span>
                  <input type="color" className="col-span-1 h-7 w-full" value={f.color} onChange={(e) => setField(i, "color", e.target.value)} data-testid={`tpl-field-${i}-color`} />
                  <span className="col-span-1 text-[10px] text-slate-400">align</span>
                  <select className="col-span-2 h-7 rounded border text-xs" value={f.align} onChange={(e) => setField(i, "align", e.target.value)} data-testid={`tpl-field-${i}-align`}>
                    <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
                  </select>
                </div>
              )}
            </div>
          ))}
        </div>

        <Button onClick={save} disabled={saving} data-testid="tpl-save"><Save size={16} /> {saving ? "Saving…" : "Save template"}</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="templates-list">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500 flex items-center gap-2">Your production templates
          <HelpHint side="right">Store all your templates here (any size &amp; bleed). Assign them to products and reuse them with client files in the <b>Fit to Template</b> tab.</HelpHint>
        </p>
        <Button size="sm" onClick={startNew} data-testid="tpl-new"><Plus size={16} /> New template</Button>
      </div>
      {list.length === 0 && <p className="text-sm text-slate-400">No templates yet. Create your first one.</p>}
      <div className="grid grid-cols-2 gap-3">
        {list.map((t) => (
          <div key={t.id} className="rounded-xl border p-3 flex items-start justify-between" data-testid={`tpl-card-${t.id}`}>
            <div>
              <div className="font-semibold flex items-center gap-2"><Layers size={15} className="text-[#2495D3]" /> {t.name}</div>
              <div className="text-xs text-slate-500 mt-1">{t.trim_w}×{t.trim_h} {t.unit} · bleed {t.bleed} · {t.sides} side{t.sides > 1 ? "s" : ""} · {(t.slots || []).length} slot(s)</div>
              {t.category && <div className="text-[11px] text-slate-400 mt-0.5">{t.category}{t.subcategory ? ` / ${t.subcategory}` : ""}</div>}
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" onClick={() => edit(t)} data-testid={`tpl-edit-${t.id}`}>Edit</Button>
              <button className="text-slate-400 hover:text-red-500 px-1" onClick={() => remove(t)} data-testid={`tpl-del-${t.id}`}><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Field = ({ label, hint, children }) => (
  <div>
    <label className="text-[11px] font-mono uppercase text-slate-500 flex items-center gap-1 mb-1">{label}{hint && <HelpHint side="top">{hint}</HelpHint>}</label>
    {children}
  </div>
);

export default TemplatesTab;
